// @flow
import React from 'react';
import {
  formatCurrency0DP,
  Percentage2DPCellRendererForStaticTable,
} from '@components/views/grid-utils/renderers';
import { formatSnakeCaseToSpacesWithCapitalizedFirstLettersCase } from '@src/common-utils/utils';
import { NotificationToastWithTable } from '@presentational/NotificationToastWithTable';
import { isCommittedCapacityLiveForConnectOrTF } from '@src/constants';

/**
 * ChangesNotifications Component
 *
 * Displays toast notifications for system-generated value changes
 * triggered when the system overwrites manual input by a Non-Admin user based on usage changes.
 *
 * Expected structure for changes:
 * [
 *   { label: "credits", dimensions: "Year 1", before: 10000, after: 5000 },
 *   { label: "global_discount", before: 15, after: 5 },
 *   { label: "connect_cc_discount_corrected", dimensions: "aws-s3_sink", before: "48.00%", after: "50.00%" }
 * ]
 *
 * Note: `dimensions` is optional, `label`, `before`, and `after` are mandatory
 */

// Convert notifications array to StaticTable format
const getTableContents = (changes) => {
  const headerRow = ['Label', 'Dimension', 'Before', 'After'];

  const dataRows = changes.map((change) => {
    const { label, dimensions, before, after } = change;
    return [label, dimensions, before, after || 'Deleted'];
  });

  return { headerRow, dataRows };
};

/**
 * ChangesNotifications Component
 * Content rendered inside the toast
 */

// Get cell renderers based on the change data
const getCellRenderers = () => {
  const beforeAndAfterColumnRenderer = (cellValue, rowIndex, colIndex, fullRow) => {
    const label = fullRow[0];
    if (label === 'credits') {
      return formatCurrency0DP.format(cellValue);
    }
    if (label === 'global_discount') {
      return Percentage2DPCellRendererForStaticTable(cellValue);
    }
    return cellValue;
  };

  return [
    (cellValue) => formatSnakeCaseToSpacesWithCapitalizedFirstLettersCase(cellValue),
    (cellValue) => cellValue || '—',
    beforeAndAfterColumnRenderer,
    beforeAndAfterColumnRenderer,
  ];
};

export const ChangesNotifications = ({ changes }) => {
  const isCommittedCapacityEnabledForConnectOrTF = isCommittedCapacityLiveForConnectOrTF();
  const ccLabel = isCommittedCapacityEnabledForConnectOrTF ? '/committed-capacity-discounts' : '';
  const notificationMessage = `The recent changes to the estimate triggered an automatic update of the below discounts/credits${ccLabel} values.\nThese changes were done because the previously applied discounts/credits${ccLabel} were outside the permitted range based on the updated usage.\nAs a result, the system has reverted these metrics to system generated discounts.\nPlease review and update the discounts if required.`;
  return (
    <NotificationToastWithTable
      cellRenderers={getCellRenderers()}
      message={notificationMessage}
      tableContents={getTableContents(changes)}
    />
  );
};
